import React, { useCallback, useEffect, useState } from 'react';
import { API_BASE_URL } from '../config';
import { getBearerAuthHeaders } from '../utils/authFetchHeaders';
import './BrokerKeyRotationBanner.css';

/**
 * "Replace your Alpaca / Coinbase API key" (security; the copy gives no reason
 * beyond "for security"). Shown on every page to
 * people whose connection is flagged (server: key_rotation_requested_at) until
 * they save a different key; then the server clears it and this goes away.
 * "Later" hides it for this browser session only.
 */
const DISMISS_KEY = 'riskloKeyRotationDismissed';
export const KEYS_SAVED_EVENT = 'risklo:broker-keys-saved';

const BROKERS = {
  alpaca: { name: 'Alpaca', where: 'https://app.alpaca.markets', whereLabel: 'Alpaca dashboard → API keys', viewOnly: false },
  coinbase: { name: 'Coinbase', where: 'https://portal.cdp.coinbase.com', whereLabel: 'Coinbase Developer Platform → API keys', viewOnly: true },
};

const stamp = (items) => items.map((i) => `${i.broker}:${i.requestedAt}`).join('|');

export default function BrokerKeyRotationBanner({ signedIn, currentPage, onNavigate }) {
  const [items, setItems] = useState([]);
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) || ''; } catch (_e) { return ''; }
  });

  const load = useCallback(async () => {
    if (!signedIn) { setItems([]); return; }
    try {
      const res = await fetch(`${API_BASE_URL}/api/broker-sync/key-rotation`, { headers: getBearerAuthHeaders() });
      if (!res.ok) return;
      const d = await res.json();
      if (d.success) setItems(d.items || []);
    } catch (_e) { /* the prompt just doesn't show */ }
  }, [signedIn]);

  // On sign-in, on every page change, when the tab regains focus, and right
  // after new keys are saved.
  useEffect(() => { load(); }, [load, currentPage]);
  useEffect(() => {
    const onFocus = () => { if (document.visibilityState === 'visible') load(); };
    window.addEventListener('focus', onFocus);
    window.addEventListener(KEYS_SAVED_EVENT, load);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener(KEYS_SAVED_EVENT, load);
    };
  }, [load]);

  if (!items.length || currentPage === 'login') return null;
  if (dismissed && dismissed === stamp(items)) return null;

  const later = () => {
    const s = stamp(items);
    try { sessionStorage.setItem(DISMISS_KEY, s); } catch (_e) { /* still hides for now */ }
    setDismissed(s);
  };

  const names = items.map((i) => BROKERS[i.broker]?.name || i.broker);
  const allPaper = items.every((i) => i.broker === 'alpaca' && i.isPaper);

  return (
    <section className="bkr-banner" role="alert" aria-labelledby="bkr-title">
      <div className="bkr-icon" aria-hidden="true">!</div>
      <div className="bkr-body">
        <h2 id="bkr-title">Security: please replace your {names.join(' and ')} API key{items.length > 1 ? 's' : ''}</h2>
        <p>
          For security, please create a new key at {names.join(' and ')}, delete the old one there, and paste
          the new one into RiskLo.
          {allPaper ? ' This is a paper account, so no real money is at risk, but the key should still be replaced.' : ''}
        </p>
        <ul className="bkr-steps">
          {items.map((i) => {
            const b = BROKERS[i.broker];
            if (!b) return null;
            return (
              <li key={i.broker}>
                <div>
                  <strong>{b.name}{i.isPaper ? ' (paper)' : ''}</strong>
                  <span>
                    {' '}New key at the <a href={b.where} target="_blank" rel="noopener noreferrer">{b.whereLabel}</a>
                    {b.viewOnly ? ' (give it View access only)' : ''}, then delete the old key there.
                  </span>
                </div>
                <button type="button" className="bkr-btn" onClick={() => onNavigate?.(`client-reporting?connect=${i.broker}`)}>
                  Paste new {b.name} key
                </button>
              </li>
            );
          })}
        </ul>
      </div>
      <button type="button" className="bkr-later" onClick={later}>Later</button>
    </section>
  );
}
